import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { eventInvitationService } from '@/services/eventInvitation.service';
import type {
  EventInvitation,
  InvitationFilters,
  CreateInvitationInput,
  RespondInvitationInput,
  PaginatedInvitationsResponse,
  PaginationMeta,
} from '@/services/eventInvitation.service';
import { ApiError } from '@/services/api';

// ==============================================
// State Interface
// ==============================================

interface EventInvitationState {
  // My received invitations
  myInvitations: EventInvitation[];
  myInvitationsMeta: PaginationMeta | null;
  pendingCount: number;

  // Event invitations (for event creator)
  eventInvitations: EventInvitation[];
  eventInvitationsMeta: PaginationMeta | null;

  // Current invitation (for detail view)
  currentInvitation: EventInvitation | null;

  // Filters
  filters: InvitationFilters;

  // Loading states
  isLoading: boolean;
  isLoadingMyInvitations: boolean;
  isLoadingEventInvitations: boolean;
  isCreating: boolean;
  isResponding: boolean;
  isDeleting: boolean;

  // Error
  error: string | null;
}

// ==============================================
// Initial State
// ==============================================

const initialState: EventInvitationState = {
  myInvitations: [],
  myInvitationsMeta: null,
  pendingCount: 0,
  eventInvitations: [],
  eventInvitationsMeta: null,
  currentInvitation: null,
  filters: {
    page: 1,
    limit: 10,
  },
  isLoading: false,
  isLoadingMyInvitations: false,
  isLoadingEventInvitations: false,
  isCreating: false,
  isResponding: false,
  isDeleting: false,
  error: null,
};

// ==============================================
// Async Thunks
// ==============================================

// Fetch my received invitations
export const fetchMyInvitations = createAsyncThunk<
  PaginatedInvitationsResponse,
  InvitationFilters | undefined,
  { rejectValue: string }
>('eventInvitations/fetchMyInvitations', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { eventInvitations: EventInvitationState };
    const mergedFilters = { ...state.eventInvitations.filters, ...filters };
    return await eventInvitationService.getMyInvitations(mergedFilters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Einladungen');
  }
});

// Fetch pending invitation count
export const fetchPendingInvitationCount = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>('eventInvitations/fetchPendingCount', async (_, { rejectWithValue }) => {
  try {
    const result = await eventInvitationService.getPendingInvitationCount();
    return result.count;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Einladungsanzahl');
  }
});

// Fetch invitations for an event (creator view)
export const fetchEventInvitations = createAsyncThunk<
  PaginatedInvitationsResponse,
  { eventId: string; filters?: InvitationFilters },
  { rejectValue: string }
>('eventInvitations/fetchEventInvitations', async ({ eventId, filters }, { rejectWithValue }) => {
  try {
    return await eventInvitationService.getEventInvitations(eventId, filters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Event-Einladungen');
  }
});

// Create invitations for an event
export const createInvitations = createAsyncThunk<
  EventInvitation[],
  { eventId: string; data: CreateInvitationInput },
  { rejectValue: string }
>('eventInvitations/createInvitations', async ({ eventId, data }, { rejectWithValue }) => {
  try {
    return await eventInvitationService.createInvitations(eventId, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen der Einladungen');
  }
});

// Respond to an invitation
export const respondToInvitation = createAsyncThunk<
  EventInvitation,
  { invitationId: string; data: RespondInvitationInput },
  { rejectValue: string }
>('eventInvitations/respondToInvitation', async ({ invitationId, data }, { rejectWithValue }) => {
  try {
    return await eventInvitationService.respondToInvitation(invitationId, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Antworten auf die Einladung');
  }
});

// Delete an invitation
export const deleteInvitation = createAsyncThunk<
  { eventId: string; invitationId: string },
  { eventId: string; invitationId: string },
  { rejectValue: string }
>('eventInvitations/deleteInvitation', async ({ eventId, invitationId }, { rejectWithValue }) => {
  try {
    await eventInvitationService.deleteInvitation(eventId, invitationId);
    return { eventId, invitationId };
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Loeschen der Einladung');
  }
});

// Get invitation by ID
export const fetchInvitationById = createAsyncThunk<
  EventInvitation,
  string,
  { rejectValue: string }
>('eventInvitations/fetchInvitationById', async (invitationId, { rejectWithValue }) => {
  try {
    return await eventInvitationService.getInvitationById(invitationId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Einladung');
  }
});

// ==============================================
// Slice
// ==============================================

const eventInvitationSlice = createSlice({
  name: 'eventInvitations',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentInvitation: (state) => {
      state.currentInvitation = null;
    },
    setFilters: (state, action: PayloadAction<Partial<InvitationFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 10,
      };
    },
    resetEventInvitations: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch my invitations
    builder
      .addCase(fetchMyInvitations.pending, (state) => {
        state.isLoadingMyInvitations = true;
        state.error = null;
      })
      .addCase(fetchMyInvitations.fulfilled, (state, action) => {
        state.isLoadingMyInvitations = false;
        state.myInvitations = action.payload.invitations;
        state.myInvitationsMeta = action.payload.meta;
      })
      .addCase(fetchMyInvitations.rejected, (state, action) => {
        state.isLoadingMyInvitations = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch pending count
    builder
      .addCase(fetchPendingInvitationCount.fulfilled, (state, action) => {
        state.pendingCount = action.payload;
      });

    // Fetch event invitations
    builder
      .addCase(fetchEventInvitations.pending, (state) => {
        state.isLoadingEventInvitations = true;
        state.error = null;
      })
      .addCase(fetchEventInvitations.fulfilled, (state, action) => {
        state.isLoadingEventInvitations = false;
        state.eventInvitations = action.payload.invitations;
        state.eventInvitationsMeta = action.payload.meta;
      })
      .addCase(fetchEventInvitations.rejected, (state, action) => {
        state.isLoadingEventInvitations = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Create invitations
    builder
      .addCase(createInvitations.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createInvitations.fulfilled, (state, action) => {
        state.isCreating = false;
        state.eventInvitations = [...action.payload, ...state.eventInvitations];
      })
      .addCase(createInvitations.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? 'Fehler beim Erstellen';
      });

    // Respond to invitation
    builder
      .addCase(respondToInvitation.pending, (state) => {
        state.isResponding = true;
        state.error = null;
      })
      .addCase(respondToInvitation.fulfilled, (state, action) => {
        state.isResponding = false;
        // Update in my invitations
        const index = state.myInvitations.findIndex((i) => i.id === action.payload.id);
        if (index !== -1) {
          state.myInvitations[index] = action.payload;
        }
        // Decrement pending count if was pending
        if (state.pendingCount > 0) {
          state.pendingCount -= 1;
        }
        // Update current invitation
        if (state.currentInvitation?.id === action.payload.id) {
          state.currentInvitation = action.payload;
        }
      })
      .addCase(respondToInvitation.rejected, (state, action) => {
        state.isResponding = false;
        state.error = action.payload ?? 'Fehler beim Antworten';
      });

    // Delete invitation
    builder
      .addCase(deleteInvitation.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteInvitation.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.eventInvitations = state.eventInvitations.filter(
          (i) => i.id !== action.payload.invitationId
        );
      })
      .addCase(deleteInvitation.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? 'Fehler beim Loeschen';
      });

    // Fetch invitation by ID
    builder
      .addCase(fetchInvitationById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInvitationById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentInvitation = action.payload;
      })
      .addCase(fetchInvitationById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });
  },
});

// ==============================================
// Export Actions
// ==============================================

export const {
  clearError,
  clearCurrentInvitation,
  setFilters,
  resetFilters,
  resetEventInvitations,
} = eventInvitationSlice.actions;

// ==============================================
// Selectors
// ==============================================

export const selectMyInvitations = (state: { eventInvitations: EventInvitationState }) =>
  state.eventInvitations.myInvitations;
export const selectMyInvitationsMeta = (state: { eventInvitations: EventInvitationState }) =>
  state.eventInvitations.myInvitationsMeta;
export const selectPendingInvitationCount = (state: { eventInvitations: EventInvitationState }) =>
  state.eventInvitations.pendingCount;
export const selectEventInvitations = (state: { eventInvitations: EventInvitationState }) =>
  state.eventInvitations.eventInvitations;
export const selectEventInvitationsMeta = (state: { eventInvitations: EventInvitationState }) =>
  state.eventInvitations.eventInvitationsMeta;
export const selectCurrentInvitation = (state: { eventInvitations: EventInvitationState }) =>
  state.eventInvitations.currentInvitation;
export const selectInvitationFilters = (state: { eventInvitations: EventInvitationState }) =>
  state.eventInvitations.filters;
export const selectInvitationLoading = (state: { eventInvitations: EventInvitationState }) => ({
  isLoading: state.eventInvitations.isLoading,
  isLoadingMyInvitations: state.eventInvitations.isLoadingMyInvitations,
  isLoadingEventInvitations: state.eventInvitations.isLoadingEventInvitations,
  isCreating: state.eventInvitations.isCreating,
  isResponding: state.eventInvitations.isResponding,
  isDeleting: state.eventInvitations.isDeleting,
});
export const selectInvitationError = (state: { eventInvitations: EventInvitationState }) =>
  state.eventInvitations.error;

// ==============================================
// Export Reducer
// ==============================================

export default eventInvitationSlice.reducer;
